import React, { useContext, useRef } from 'react'
import { useIntl } from 'react-intl'
import { App, Progress } from 'antd'

import * as logger from '../../../../services/logger'
import { postCoreEvent } from '../../../../services/core-event'
import { notificationStore } from '../../../../context/notificationsContext/store'

export type MoveEventType = 'TEAM_USER_MOVED' | 'LOCATION_USER_MOVED'

const MOVE_CONCURRENCY = 8

export interface IBulkUserMoveParams {
  eventType: MoveEventType
  targetId: string
  targetName: string
  /** Already filtered to the people who actually change - anyone already there is not re-posted. */
  users: { id: string }[]
  progressMessageId?: string
  errorMessageId?: string
}

/**
 * Moves a set of users to one department or location.
 *
 * One event per user, chunked, all sharing a single correlation id so the websocket handler can
 * track them as one group and draw a single progress bar. A partial failure shrinks that group
 * rather than leaving it waiting for events that will never arrive.
 */
export function useBulkUserMove(): (params: IBulkUserMoveParams) => Promise<{ failedIds: string[] }> {
  const { formatMessage } = useIntl()
  const { notification } = App.useApp()
  const { eventsNotifications, setEventsNotifications } = useContext(notificationStore)
  const eventsNotificationsRef = useRef(eventsNotifications)
  eventsNotificationsRef.current = eventsNotifications

  return async ({ eventType, targetId, targetName, users, progressMessageId, errorMessageId }) => {
    const groupId = `bulk-move-${crypto.randomUUID()}`

    notification.open({
      key: groupId,
      message: formatMessage({ id: progressMessageId ?? 'users.table.bulkMoveInProgress' }, { count: users.length, name: targetName }),
      description: React.createElement(Progress, { percent: 0, size: 'small' }),
      duration: 0,
    })
    setEventsNotifications({
      ...eventsNotificationsRef.current,
      [groupId]: {
        eventType,
        payload: { users: users.map((user) => user.id), totalUsers: users.length, name: targetName },
      },
    })

    const failedIds: string[] = []
    for (let offset = 0; offset < users.length; offset += MOVE_CONCURRENCY) {
      const chunk = users.slice(offset, offset + MOVE_CONCURRENCY)
      await Promise.all(
        chunk.map(async (user) => {
          try {
            await postCoreEvent(
              {
                eventType,
                eventGroup: 'USER',
                userId: user.id,
                correlationId: groupId,
                ...(eventType === 'TEAM_USER_MOVED' ? { teamId: targetId } : { locationId: targetId }),
              },
              'users-table'
            )
          } catch (error) {
            logger.warning({ errorType: 'ERROR BULK MOVE', error })
            failedIds.push(user.id)
          }
        })
      )
    }

    if (failedIds.length > 0) {
      notification.error({ message: formatMessage({ id: errorMessageId ?? 'error.generic' }) })
      const current = eventsNotificationsRef.current
      const group = current[groupId]
      if (group) {
        const remaining = group.payload.users.filter((id: string) => !failedIds.includes(id))
        if (remaining.length === 0) {
          notification.destroy(groupId)
          const next = { ...current }
          delete next[groupId]
          setEventsNotifications(next)
        } else {
          setEventsNotifications({
            ...current,
            [groupId]: {
              ...group,
              payload: { ...group.payload, users: remaining, totalUsers: group.payload.totalUsers - failedIds.length },
            },
          })
        }
      }
    }

    return { failedIds }
  }
}
